// Navigation for the Secure AI Adoption header (SecurityDesktopMenu / SecurityMobileMenu).
// The legacy menus in src/components/partials keep their own hard-coded items untouched.
import { ROUTES } from '@/content/security/routes';
// Learning Hub card image: views/resources' learninghub-banner-desktop.svg (2.2 MB) rendered
// once to a 34 KB webp for the menu.
import learningHubImage from '@/assets/img/security/learning-hub-menu.webp';

// Plain top-level links, in display order (About Us first; "For Job Seekers" is a plain link
// now, no dropdown, going straight to the Join our Talent Pool page).
export const primaryLinks = [
  { title: 'About Us', href: ROUTES.about },
  { title: 'Services', href: ROUTES.services },
  { title: 'Maturity Roadmap', href: ROUTES.roadmap },
  { title: 'Security Baseline', href: ROUTES.baseline },
  { title: 'For Job Seekers', href: ROUTES.careers },
];

// Replaces the old "Schedule a Meeting" button.
export const primaryCta = { title: 'Score yourself', href: ROUTES.score };

// The only dropdown left in security mode, same items and copy as DesktopMenu.vue.
// `icon` is an mdi icon for the grid tile; the `featured` item (with `image`) is shown as a
// picture card beside the grid in SecurityDesktopMenu.
export const dropdowns = [
  {
    key: 'resources',
    title: 'Resources',
    items: [
      {
        title: 'Blogs and Insights',
        description: 'Stay ahead with expert insights and industry trends',
        href: '/blogs-and-insights',
        icon: 'mdi-post-outline',
      },
      {
        title: 'Experience Nepal',
        description: "Dive into Nepal's traditions, celebrations, and unique stories",
        href: '/experience-nepal',
        icon: 'mdi-image-filter-hdr',
      },
      {
        title: 'Learning Hub',
        description: 'Explore webinars, workshops, and expert-led sessions',
        href: '/learning-hub',
        icon: 'mdi-school-outline',
        featured: true,
        image: learningHubImage,
      },
      {
        title: 'Upcoming Events',
        description: 'Stay updated on workshops, panels, and networking events',
        href: '/events',
        icon: 'mdi-calendar-star',
      },
      {
        title: 'Trainings and Certifications',
        description: 'Gain new skills with expert-led training and certification',
        href: '/trainings-and-certifications',
        icon: 'mdi-certificate-outline',
      },
      {
        // Copy as in the legacy For Businesses dropdown.
        title: 'Customer Stories',
        description: 'See how DIGU has helped businesses succeed',
        href: '/customers',
        icon: 'mdi-handshake-outline',
      },
    ],
  },
];

// Hidden in security mode. The routes stay registered, so old links keep working.
// To show one again, move it into `dropdowns` above.
export const hiddenLegacyDropdowns = [
  {
    // Replaced by the plain "For Job Seekers" link (Join our Talent Pool) in primaryLinks.
    key: 'jobSeekers',
    title: 'For Job Seekers',
    items: [
      {
        title: 'Why DIGU for Career Opportunities',
        description: 'Unlock global opportunities while staying in Nepal',
        href: '/why-digu-for-career-opportunities',
      },
      {
        title: 'Learn about our Remote Career Acceleration Program',
        description: 'Advance your career through DIGU',
        href: '/remote-career',
      },
      {
        title: 'Verified Badges',
        description: 'Earn credentials that highlight your expertise to employers',
        href: '/verified-badges',
      },
      {
        title: 'Join our Talent Pool',
        description: 'Be part of DIGU’s Talent Pool and unlock job opportunities',
        href: '/talents-on-demand',
      },
    ],
  },
  {
    key: 'diguCares',
    title: 'DIGU Cares',
    items: [
      {
        title: 'Grassroot Impact Initiative',
        description: 'Empowering Nepali talent through education, training, and careers',
        href: '/grassroot-impact-initative',
      },
      {
        title: 'Impact Stories',
        description: 'Real stories of lives transformed through DIGU’s initiatives',
        href: '/impact-stories',
      },
    ],
  },
  {
    key: 'business',
    title: 'For Businesses',
    items: [
      {
        title: 'Why DIGU for Businesses',
        description: 'Build apps and scale teams with cost-effective, scalable solutions',
        href: '/why-digu-for-businesses',
      },
      { title: 'Customer Stories', description: 'See how DIGU has helped businesses succeed', href: '/customers' },
    ],
  },
  {
    key: 'solutions',
    title: 'Our Solutions',
    grid: true,
    items: [
      { title: 'Design My App', description: 'User-friendly UX/UI design for seamless experiences', href: '/design-my-app' },
      { title: 'Roadmap My App', description: 'Plan your app’s future with a strategic roadmap', href: '/roadmap-my-app' },
      { title: 'Build My App', description: 'Launch faster with a scalable, market-ready MVP', href: '/build-my-app' },
      { title: 'Staff Augmentation', description: 'Scale your team with top tech talent on demand', href: '/staff-augmentation' },
      { title: 'Review My App', description: 'Optimize performance, security, and scalability', href: '/review-my-app' },
      {
        title: 'Identity Management for My App',
        description: 'Secure, scalable identity solutions powered by Okta',
        href: '/implement-identity-management',
      },
    ],
  },
];
